import { useState, useRef, useMemo, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import ShopPicker, { type ShopSelection } from '../components/ShopPicker'
import CameraCapture from '../components/CameraCapture'
import { submitFree, addPhoto } from '../api/submissions'
import { listCategories } from '../api/categories'
import { getLocation } from '../utils/geolocation'
import SuccessOverlay from '../components/SuccessOverlay'
import type { Category } from '../types'

// Camera icon SVG (same as SubmissionPage)
function CameraIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3.2" fill="rgba(255,255,255,0.7)" />
      <path
        d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"
        fill="rgba(255,255,255,0.7)"
      />
    </svg>
  )
}

export default function FreeSubmissionPage() {
  const { t } = useTranslation(['worker', 'common'])
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const preselectedMerchantId = searchParams.get('merchant_id')
  const preselectedShopName = searchParams.get('shop_name')

  const [shop, setShop] = useState<ShopSelection | null>(
    preselectedMerchantId && preselectedShopName
      ? { shopName: preselectedShopName, selectedMerchantId: preselectedMerchantId }
      : null
  )
  const [productName, setProductName] = useState('')
  const [description, setDescription] = useState('')
  const [brand, setBrand] = useState('')
  const [priceUzs, setPriceUzs] = useState('')
  const [currency, setCurrency] = useState<'UZS' | 'USD'>('UZS')
  const [categoryId, setCategoryId] = useState('')
  const [keywordsRaw, setKeywordsRaw] = useState('')
  const [stallSection, setStallSection] = useState('')
  const [comment, setComment] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [gpsLat, setGpsLat] = useState<number | null>(null)
  const [gpsLng, setGpsLng] = useState<number | null>(null)
  const [gpsLoading, setGpsLoading] = useState(false)
  const [gpsError, setGpsError] = useState<string | null>(null)
  const [showMoreDetails, setShowMoreDetails] = useState(false)
  const [showCamera, setShowCamera] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    listCategories().then(setCategories).catch(() => {})
  }, [])

  const photoPreviews = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos])

  useEffect(() => {
    return () => { photoPreviews.forEach((url) => URL.revokeObjectURL(url)) }
  }, [photoPreviews])

  const handlePhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    setPhotos((prev) => [...prev, ...files].slice(0, 5))
    e.target.value = ''
  }

  const removePhoto = (i: number) => {
    setPhotos((prev) => prev.filter((_, idx) => idx !== i))
  }

  const handleGps = async () => {
    setGpsLoading(true)
    setGpsError(null)
    try {
      const { latitude, longitude } = await getLocation()
      setGpsLat(latitude)
      setGpsLng(longitude)
    } catch (e: unknown) {
      setGpsError(e instanceof Error ? e.message : t('common:error.failedToLoad'))
    } finally {
      setGpsLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!shop || !productName.trim() || !priceUzs) return
    setError(null)
    setIsSubmitting(true)
    try {
      const keywords = keywordsRaw
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean)

      const parsedPrice = parseFloat(priceUzs)
      const submission = await submitFree({
        shop_name: shop.shopName,
        product_name: productName.trim(),
        lang: i18n.language || 'ru',
        description: description.trim() || undefined,
        price_uzs: currency === 'UZS' ? parsedPrice : undefined,
        price_usd: currency === 'USD' ? parsedPrice : undefined,
        brand: brand.trim() || undefined,
        category_id: categoryId || undefined,
        keywords: keywords.length > 0 ? keywords : undefined,
        stall_section: stallSection.trim() || undefined,
        shop_latitude: gpsLat ?? shop.shopLat,
        shop_longitude: gpsLng ?? shop.shopLng,
        shop_phone: shop.shopPhone,
        shop_address: shop.shopAddress,
        shop_category: shop.shopCategory,
        shop_description: shop.shopDescription,
        selected_merchant_id: shop.selectedMerchantId,
        comment: comment.trim() || undefined,
      })

      for (const photo of photos) {
        await addPhoto(submission.id, photo)
      }

      setShowSuccess(true)
      const merchantId = shop.selectedMerchantId
      setTimeout(() => {
        if (merchantId) navigate(`/shops/${merchantId}`)
        else navigate('/')
      }, 1800)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('common:error.generic'))
      setIsSubmitting(false)
    }
  }

  const canSubmit = !!shop && !!productName.trim() && !!priceUzs && !isSubmitting
  const hasPhotos = photoPreviews.length > 0

  const inputStyle: React.CSSProperties = {
    background: 'var(--app-card-bg)',
    color: 'var(--tg-theme-text-color)',
    WebkitTextFillColor: 'var(--tg-theme-text-color)',
    caretColor: 'var(--tg-theme-text-color)',
    border: '1.5px solid transparent',
    borderRadius: '14px',
    padding: '12px 14px',
    width: '100%',
    fontSize: '16px',
    outline: 'none',
    boxSizing: 'border-box',
  }

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--tg-theme-text-color)',
    opacity: 0.5,
    marginBottom: '6px',
    display: 'block',
  }

  return (
    <div className="min-h-screen safe-area-bottom" style={{ background: 'var(--app-page-bg)' }}>
      {showSuccess && <SuccessOverlay />}

      {/* ── Header ─────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 pt-4 pb-3"
        style={{ background: 'var(--app-card-bg)' }}
      >
        <button
          className="text-xl leading-none opacity-50 flex-shrink-0"
          style={{ color: 'var(--tg-theme-text-color)' }}
          onClick={() => navigate(-1)}
        >
          ←
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-base" style={{ color: 'var(--tg-theme-text-color)' }}>
            {t('freeSubmission.title')}
          </h1>
          <p className="text-xs opacity-40 mt-0.5" style={{ color: 'var(--tg-theme-text-color)' }}>
            {t('freeSubmission.subtitle')}
          </p>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* ── Photo zone ───────────────────────────────────── */}
        <div>
          {showCamera && (
            <CameraCapture
              onCapture={(file) => {
                setPhotos((prev) => [...prev, file].slice(0, 5))
                setShowCamera(false)
              }}
              onClose={() => setShowCamera(false)}
            />
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handlePhotoAdd}
          />
          {!hasPhotos ? (
            <div
              className="w-full rounded-2xl flex flex-col items-center justify-center gap-3"
              style={{ height: 180, background: '#1e2540' }}
            >
              <div className="flex gap-3">
                <button
                  className="flex flex-col items-center gap-1.5"
                  style={{ background: 'none', border: 'none' }}
                  onClick={() => setShowCamera(true)}
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.12)' }}
                  >
                    <CameraIcon />
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 500 }}>
                    {t('submission.takePhoto')}
                  </span>
                </button>
                <button
                  className="flex flex-col items-center gap-1.5"
                  style={{ background: 'none', border: 'none' }}
                  onClick={() => fileRef.current?.click()}
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.12)' }}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="7" height="7" rx="1" fill="rgba(255,255,255,0.7)" />
                      <rect x="14" y="3" width="7" height="7" rx="1" fill="rgba(255,255,255,0.7)" />
                      <rect x="3" y="14" width="7" height="7" rx="1" fill="rgba(255,255,255,0.7)" />
                      <rect x="14" y="14" width="7" height="7" rx="1" fill="rgba(255,255,255,0.7)" />
                    </svg>
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 500 }}>
                    {t('submission.gallery')}
                  </span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {photoPreviews.map((url, i) => (
                <div key={i} className="relative flex-shrink-0">
                  <img src={url} alt="" className="w-20 h-20 rounded-xl object-cover" />
                  <button
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center text-white"
                    style={{ background: '#e53935' }}
                    onClick={() => removePhoto(i)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <button
                  className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl font-bold"
                  style={{ background: '#1e2540', color: 'rgba(255,255,255,0.5)', border: 'none' }}
                  onClick={() => setShowCamera(true)}
                >
                  +
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Product name ─────────────────────────────────── */}
        <div>
          <label style={labelStyle}>{t('freeSubmission.productName')}</label>
          <input
            style={{ ...inputStyle, border: '1.5px solid var(--tg-theme-button-color)' }}
            placeholder={t('freeSubmission.productNamePlaceholder')}
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            autoFocus
          />
        </div>

        {/* ── Price ────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label style={{ ...labelStyle, marginBottom: 0 }}>{t('submission.price')}</label>
            <div className="flex rounded-xl overflow-hidden" style={{ background: 'var(--app-card-bg)' }}>
              {(['UZS', 'USD'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  className="px-3 py-1 text-xs font-semibold"
                  style={{
                    background: currency === c ? 'var(--tg-theme-button-color)' : 'transparent',
                    color: currency === c ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-hint-color)',
                  }}
                  onClick={() => setCurrency(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <input
            style={{
              ...inputStyle,
              fontSize: '20px',
              fontWeight: '600',
              border: '1.5px solid var(--tg-theme-button-color)',
            }}
            placeholder="0"
            type="number"
            inputMode="numeric"
            min="0"
            value={priceUzs}
            onChange={(e) => setPriceUzs(e.target.value)}
          />
        </div>

        {/* ── Shop ─────────────────────────────────────────── */}
        <div>
          <label style={labelStyle}>{t('submission.shop')}</label>
          {preselectedMerchantId && preselectedShopName ? (
            <div
              className="rounded-2xl p-3 flex items-center gap-3"
              style={{ background: 'var(--app-card-bg)' }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                style={{ background: 'var(--app-page-bg)' }}
              >
                🏪
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--tg-theme-text-color)' }}>
                  {preselectedShopName}
                </p>
                <p className="text-xs mt-0.5 font-medium" style={{ color: '#22c55e' }}>
                  {t('freeSubmission.linkedShop')}
                </p>
              </div>
            </div>
          ) : (
            <ShopPicker value={shop} onChange={setShop} onClear={() => setShop(null)} />
          )}
        </div>

        {/* ── Stall / section ──────────────────────────────── */}
        <div>
          <label style={labelStyle}>{t('submission.stallSection')}</label>
          <input
            style={inputStyle}
            placeholder={t('submission.stallSectionPlaceholder')}
            value={stallSection}
            onChange={(e) => setStallSection(e.target.value)}
          />
        </div>

        {/* ── Collapsible more details ──────────────────────── */}
        <button
          className="text-sm font-medium flex items-center gap-1.5"
          style={{ color: 'var(--tg-theme-link-color)', background: 'none', border: 'none' }}
          onClick={() => setShowMoreDetails((v) => !v)}
        >
          <span>{showMoreDetails ? '▼' : '▶'}</span>
          <span>{t('submission.moreDetails')}</span>
        </button>

        {showMoreDetails && (
          <div className="space-y-3">
            {/* Description */}
            <div>
              <label style={labelStyle}>{t('freeSubmission.descriptionLabel')}</label>
              <textarea
                style={{ ...inputStyle, minHeight: '64px', resize: 'none' }}
                placeholder={t('freeSubmission.descriptionPlaceholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Brand */}
            <div>
              <label style={labelStyle}>{t('freeSubmission.brandLabel')}</label>
              <input
                style={inputStyle}
                placeholder={t('freeSubmission.brandPlaceholder')}
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              />
            </div>

            {/* Category */}
            {categories.length > 0 && (
              <div>
                <label style={labelStyle}>{t('freeSubmission.categoryLabel')}</label>
                <select
                  style={{
                    ...inputStyle,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    cursor: 'pointer',
                    color: categoryId ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)',
                    WebkitTextFillColor: categoryId
                      ? 'var(--tg-theme-text-color)'
                      : 'var(--tg-theme-hint-color)',
                  } as React.CSSProperties}
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="">{t('freeSubmission.noCategory')}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Keywords */}
            <div>
              <label style={labelStyle}>{t('freeSubmission.keywordsLabel')}</label>
              <input
                style={inputStyle}
                placeholder={t('freeSubmission.keywordsPlaceholder')}
                value={keywordsRaw}
                onChange={(e) => setKeywordsRaw(e.target.value)}
              />
            </div>

            {/* GPS location */}
            <div>
              <label style={labelStyle}>{t('freeSubmission.gpsLabel')}</label>
              <button
                className="w-full py-3 rounded-2xl text-sm font-medium"
                style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
                onClick={handleGps}
                disabled={gpsLoading}
              >
                {gpsLoading
                  ? t('freeSubmission.gpsLoading')
                  : gpsLat != null
                  ? t('freeSubmission.gpsRecapture', { lat: gpsLat.toFixed(4), lng: gpsLng!.toFixed(4) })
                  : t('freeSubmission.gpsCapture')}
              </button>
              {gpsError && (
                <p className="text-xs mt-1 text-center" style={{ color: '#ef4444' }}>
                  {gpsError}
                </p>
              )}
            </div>

            {/* Comment */}
            <div>
              <label style={labelStyle}>{t('freeSubmission.notesLabel')}</label>
              <textarea
                style={{ ...inputStyle, minHeight: '64px', resize: 'none' }}
                placeholder={t('freeSubmission.notesPlaceholder')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          </div>
        )}

        {error && (
          <div
            className="rounded-xl px-4 py-3 text-sm"
            style={{ background: '#ef444418', color: '#ef4444' }}
          >
            {error}
          </div>
        )}

        {/* ── Submit button ─────────────────────────────────── */}
        <button
          className="w-full py-4 rounded-2xl font-bold text-base disabled:opacity-50 flex items-center justify-center gap-2 mb-6"
          style={{
            background: canSubmit ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-hint-color)',
            color: 'var(--tg-theme-button-text-color)',
          }}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {isSubmitting ? (
            t('freeSubmission.submitting')
          ) : (
            <>
              {t('freeSubmission.submitProduct')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18l6-6-6-6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  )
}
